/**
 * Remote Document Mapping
 *
 * Handles mapping remote API documents to local Lot format with AI analysis integration
 * @module portal/modules/lotOperations/mapping
 */

import { Lot } from '../types';
import {
  parseCurrency as parseCurrencyValue,
  roundToTwo,
  formatUsd as formatCurrencyDisplay,
} from '@lotlingo/shared';
import {
  AI_ANALYSIS_KEYS,
  canonicalizeAiKey,
  extractAiValue,
  normalizeListings,
  MAX_AI_EXTRACTION_DEPTH,
} from '@lotlingo/shared';
import { createLogger } from '../../../shared/logger';
import { unescapeHtml } from '../../../shared/htmlUtils';

const log = createLogger('portal:mapping');

// Use re-exported utilities from shared module
function formatAiRawAmount(amount: number): string {
  return formatCurrencyDisplay(amount) ?? '';
}

function normalizeAiCurrency(candidate: any): { amount: number; raw: string } | undefined {
  if (candidate == null) return undefined;
  if (typeof candidate === 'number' && Number.isFinite(candidate)) {
    const normalizedAmount = roundToTwo(candidate);
    return { amount: normalizedAmount, raw: formatAiRawAmount(normalizedAmount) };
  }
  if (typeof candidate === 'string') {
    const trimmed = candidate.trim();
    if (!trimmed) return undefined;
    const parsed = parseCurrencyValue(trimmed);
    if (parsed == null) return undefined;
    const normalizedAmount = roundToTwo(parsed);
    const hasCurrencySymbol = /[$€£¥]/.test(trimmed);
    const raw = hasCurrencySymbol ? trimmed : formatAiRawAmount(normalizedAmount);
    return { amount: normalizedAmount, raw };
  }
  if (Array.isArray(candidate)) {
    for (let i = 0; i < candidate.length; i++) {
      const normalized = normalizeAiCurrency(candidate[i]);
      if (normalized) return normalized;
    }
    return undefined;
  }
  if (typeof candidate === 'object') {
    const amountFields = ['amount', 'value', 'estimate', 'high', 'low', 'min', 'max'];
    for (let i = 0; i < amountFields.length; i++) {
      const key = amountFields[i];
      if (Object.prototype.hasOwnProperty.call(candidate, key)) {
        const normalized = normalizeAiCurrency(candidate[key]);
        if (normalized) {
          if (!normalized.raw && typeof candidate.raw === 'string' && candidate.raw.trim()) {
            normalized.raw = candidate.raw.trim();
          }
          return normalized;
        }
      }
    }
    if (typeof candidate.raw === 'string') {
      const normalizedRaw = normalizeAiCurrency(candidate.raw);
      if (normalizedRaw) return normalizedRaw;
    }
    const values = Object.values(candidate);
    for (let j = 0; j < values.length; j++) {
      const normalized = normalizeAiCurrency(values[j]);
      if (normalized) return normalized;
    }
  }
  return undefined;
}

function extractAiCurrency(
  analysis: any,
  keys: string[]
): { amount: number; raw: string } | undefined {
  const candidate = extractAiValue(analysis, keys);
  return normalizeAiCurrency(candidate);
}

function extractAiListings(analysis: any, keys: string[]): string[] | undefined {
  const candidate = extractAiValue(analysis, keys);
  return normalizeListings(candidate);
}

function extractAiStatus(analysis: any, keys: string[]): string | undefined {
  const candidate = extractAiValue(analysis, keys);
  if (typeof candidate === 'string') {
    const trimmed = candidate.trim();
    return trimmed || undefined;
  }
  return undefined;
}

function mapPriceRetry(retries: any): any {
  if (!retries || typeof retries !== 'object') return undefined;
  const normalized: any = {};
  const count = typeof retries.count === 'number' ? retries.count : Number(retries.count);
  if (Number.isFinite(count)) normalized.count = count;
  if (typeof retries.lastAttemptedAt === 'string')
    normalized.lastAttemptedAt = retries.lastAttemptedAt;
  if (typeof retries.lastAttemptedBy === 'string')
    normalized.lastAttemptedBy = retries.lastAttemptedBy;
  if (typeof retries.lastPortalUserId === 'string')
    normalized.lastPortalUserId = retries.lastPortalUserId;
  if (typeof retries.lastPortalUserEmail === 'string')
    normalized.lastPortalUserEmail = retries.lastPortalUserEmail;
  if (typeof retries.lastPortalUserName === 'string')
    normalized.lastPortalUserName = retries.lastPortalUserName;
  return Object.keys(normalized).length ? normalized : undefined;
}

function mapPriceReview(review: any): any {
  if (!review || typeof review !== 'object') return undefined;
  const normalized: any = {};
  if (typeof review.status === 'string') normalized.status = review.status;
  if (typeof review.approved === 'boolean') normalized.approved = review.approved;
  if (typeof review.updatedAt === 'string') normalized.updatedAt = review.updatedAt;
  if (typeof review.reviewerPortalName === 'string')
    normalized.reviewerPortalName = review.reviewerPortalName;
  if (typeof review.reviewerPortalEmail === 'string')
    normalized.reviewerPortalEmail = review.reviewerPortalEmail;
  if (typeof review.reviewerPortalUserId === 'string')
    normalized.reviewerPortalUserId = review.reviewerPortalUserId;
  if (typeof review.reviewerUserId === 'string') normalized.reviewerUserId = review.reviewerUserId;
  const retries = mapPriceRetry(review.retries);
  if (retries) normalized.retries = retries;
  return Object.keys(normalized).length ? normalized : undefined;
}

/**
 * Map remote document to local lot format
 */
export function mapRemoteDocument(doc: any): Lot | null {
  if (!doc || typeof doc !== 'object') return null;
  if (doc.document && typeof doc.document === 'object') return mapRemoteDocument(doc.document);
  let shared = doc.lot && typeof doc.lot === 'object' ? doc.lot : null;
  if (!shared && doc.shared && typeof doc.shared === 'object') shared = doc.shared;
  let overlay = doc.overlay && typeof doc.overlay === 'object' ? doc.overlay : null;
  if (!overlay && doc.userOverlay && typeof doc.userOverlay === 'object') overlay = doc.userOverlay;

  log.debug('doc.overlay:', doc.overlay);
  log.debug('overlay bidding:', overlay?.bidding, 'bidAmount:', overlay?.bidAmount);

  const isComposedDoc = Boolean(
    doc && doc.payload && !doc.lot && !doc.shared && !doc.overlay && !doc.userOverlay
  );
  const legacyDoc = !shared && doc.payload && typeof doc.payload === 'object' ? doc : null;
  if (!shared && legacyDoc) {
    shared = {
      id: legacyDoc.id || legacyDoc.lotId || legacyDoc.itemNumber || '',
      itemNumber:
        legacyDoc.itemNumber ||
        legacyDoc.payload?.itemNumber ||
        legacyDoc.payload?.lotNumber ||
        legacyDoc.id ||
        '',
      payload: legacyDoc.payload || {},
      payloadHash: legacyDoc.payloadHash || '',
      aiAnalysis: legacyDoc.aiAnalysis,
      aiStatus: legacyDoc.aiStatus,
      aiAttempts: legacyDoc.aiAttempts,
      aiError: legacyDoc.aiError,
      aiModel: legacyDoc.aiModel,
      aiTruncatedPaths: legacyDoc.aiTruncatedPaths,
      aiRequestedAt: legacyDoc.aiRequestedAt,
      aiCompletedAt: legacyDoc.aiCompletedAt,
      createdAt: legacyDoc.createdAt,
      updatedAt: legacyDoc.updatedAt,
    };
  }
  if (!overlay && legacyDoc) {
    overlay = {
      id: legacyDoc.id || (shared && shared.id) || '',
      userId: legacyDoc.userId || '',
      lotId: (shared && shared.id) || legacyDoc.lotId || legacyDoc.id || '',
      itemNumber:
        (shared && shared.itemNumber) ||
        legacyDoc.itemNumber ||
        legacyDoc.payload?.itemNumber ||
        '',
      auctionId: legacyDoc.payload?.auctionId,
      source: legacyDoc.payload?.source,
      payloadHash:
        shared?.payloadHash !== undefined ? shared.payloadHash : legacyDoc.payloadHash || '',
      status: legacyDoc.status || 'received',
      favorite: legacyDoc.favorite,
      deleted: legacyDoc.deleted,
      bidding: legacyDoc.bidding,
      bidAmount: legacyDoc.bidAmount,
      highSellOverride: undefined,
      lowSellOverride: undefined,
      priceReview: legacyDoc.priceReview,
      createdAt: legacyDoc.createdAt,
      updatedAt: legacyDoc.updatedAt,
      processedAt: legacyDoc.processedAt,
    };
  }
  if (!shared || !shared.payload) return null;
  const payload = typeof shared.payload === 'object' && shared.payload ? shared.payload : {};
  let aiAnalysis =
    shared.aiAnalysis && typeof shared.aiAnalysis === 'object' ? shared.aiAnalysis : undefined;
  if (!aiAnalysis && legacyDoc?.aiAnalysis && typeof legacyDoc.aiAnalysis === 'object')
    aiAnalysis = legacyDoc.aiAnalysis;
  function cloneValue(value: any): any {
    if (value == null) return value;
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return value;
    }
  }
  function parseTimestamp(value: any): number | undefined {
    if (typeof value !== 'string') return undefined;
    const ts = Date.parse(value);
    return Number.isFinite(ts) ? ts : undefined;
  }
  const bidValue = payload.currentBid;
  const bidAmount = bidValue && typeof bidValue.amount === 'number' ? bidValue.amount : undefined;
  const bidRaw =
    bidValue && typeof bidValue.raw === 'string'
      ? bidValue.raw
      : typeof payload.currentBid === 'string'
        ? payload.currentBid
        : undefined;
  const nextBidValue = payload.nextRequiredBid;
  const nextBidAmount =
    nextBidValue && typeof nextBidValue.amount === 'number' ? nextBidValue.amount : undefined;
  const nextBidRaw =
    nextBidValue && typeof nextBidValue.raw === 'string'
      ? nextBidValue.raw
      : typeof payload.nextRequiredBid === 'string'
        ? payload.nextRequiredBid
        : undefined;
  function parseCurrency(
    preferred: any,
    fallback: any
  ): { amount: number | undefined; raw: string | undefined } {
    if (preferred === null) return { amount: undefined, raw: undefined };
    const source = preferred !== undefined ? preferred : fallback;
    let amount;
    let raw;
    if (source && typeof source === 'object') {
      if (typeof source.amount === 'number') amount = source.amount;
      if (typeof source.raw === 'string') raw = source.raw;
    } else if (typeof source === 'number') {
      amount = source;
      raw = source.toFixed ? source.toFixed(2) : String(source);
    } else if (typeof source === 'string') {
      raw = source;
    }
    return { amount, raw };
  }
  const lowCurrency = parseCurrency(
    overlay ? overlay.lowSellOverride : undefined,
    payload.lowSellValue
  );
  const allowAiCurrencyFallback = !isComposedDoc;
  if (lowCurrency.amount === undefined && aiAnalysis && allowAiCurrencyFallback) {
    const aiLowEstimate = extractAiCurrency(aiAnalysis, AI_ANALYSIS_KEYS.lowPrice);
    if (aiLowEstimate) {
      if (typeof aiLowEstimate.amount === 'number' && Number.isFinite(aiLowEstimate.amount))
        lowCurrency.amount = aiLowEstimate.amount;
      if (!lowCurrency.raw && aiLowEstimate.raw) lowCurrency.raw = aiLowEstimate.raw;
    }
  }
  const highCurrency = parseCurrency(
    overlay ? overlay.highSellOverride : undefined,
    payload.highSellValue
  );
  if (highCurrency.amount === undefined && aiAnalysis && allowAiCurrencyFallback) {
    const aiHighEstimate = extractAiCurrency(aiAnalysis, AI_ANALYSIS_KEYS.highPrice);
    if (aiHighEstimate) {
      if (typeof aiHighEstimate.amount === 'number' && Number.isFinite(aiHighEstimate.amount))
        highCurrency.amount = aiHighEstimate.amount;
      if (!highCurrency.raw && aiHighEstimate.raw) highCurrency.raw = aiHighEstimate.raw;
    }
  }
  let closeRaw;
  const closeValue = payload.closeDateTime;
  if (closeValue && typeof closeValue === 'object') closeRaw = closeValue.iso || closeValue.raw;
  else if (typeof closeValue === 'string') closeRaw = closeValue;
  let closeAt = closeRaw ? Date.parse(closeRaw) : undefined;
  if (!Number.isFinite(closeAt)) closeAt = undefined;
  let lowListings = normalizeListings(payload.lowListings);
  if ((!lowListings || !lowListings.length) && aiAnalysis) {
    const aiLowListings = extractAiListings(aiAnalysis, AI_ANALYSIS_KEYS.lowListings);
    if (aiLowListings) lowListings = normalizeListings(aiLowListings) || lowListings;
  }
  let highListings = normalizeListings(payload.highListings);
  if ((!highListings || !highListings.length) && aiAnalysis) {
    const aiHighListings = extractAiListings(aiAnalysis, AI_ANALYSIS_KEYS.highListings);
    if (aiHighListings) highListings = normalizeListings(aiHighListings) || highListings;
  }
  let priceStatusValue = typeof payload.priceStatus === 'string' ? payload.priceStatus : undefined;
  if ((priceStatusValue == null || priceStatusValue === '') && aiAnalysis) {
    const aiStatus = extractAiStatus(aiAnalysis, AI_ANALYSIS_KEYS.priceStatus);
    if (aiStatus) priceStatusValue = aiStatus;
  }
  if (priceStatusValue === 'failedPrice') {
    const hasAiPricing =
      (typeof lowCurrency.amount === 'number' && Number.isFinite(lowCurrency.amount)) ||
      (typeof highCurrency.amount === 'number' && Number.isFinite(highCurrency.amount)) ||
      (lowListings && lowListings.length) ||
      (highListings && highListings.length);
    if (hasAiPricing) priceStatusValue = 'successPrice';
  }
  const overlayCreatedAt = overlay?.createdAt ? parseTimestamp(overlay.createdAt) : undefined;
  const overlayUpdatedAt = overlay?.updatedAt ? parseTimestamp(overlay.updatedAt) : undefined;
  const sharedCreatedAt = shared?.createdAt ? parseTimestamp(shared.createdAt) : undefined;
  const sharedUpdatedAt = shared?.updatedAt ? parseTimestamp(shared.updatedAt) : undefined;
  // Decode HTML entities (e.g., &quot; -> ") that may come from auction site sources
  const rawTitle = payload.title || payload.lotTitle || 'Untitled lot';
  const title = unescapeHtml(rawTitle);
  const source = overlay?.source ? overlay.source : payload.source || 'generic';
  const auctionId =
    overlay && typeof overlay.auctionId === 'string' && overlay.auctionId
      ? overlay.auctionId
      : payload.auctionId || '';
  const legacyAiAttempts =
    legacyDoc && typeof legacyDoc.aiAttempts === 'number' ? legacyDoc.aiAttempts : undefined;
  const legacyAiStatus =
    legacyDoc && typeof legacyDoc.aiStatus === 'string' ? legacyDoc.aiStatus : undefined;
  const legacyAiRequestedAt =
    legacyDoc && typeof legacyDoc.aiRequestedAt === 'string' ? legacyDoc.aiRequestedAt : undefined;
  const legacyAiCompletedAt =
    legacyDoc && typeof legacyDoc.aiCompletedAt === 'string' ? legacyDoc.aiCompletedAt : undefined;
  const legacyAiError =
    legacyDoc && typeof legacyDoc.aiError === 'string' ? legacyDoc.aiError : undefined;
  const legacyAiAnalysis =
    legacyDoc && legacyDoc.aiAnalysis !== undefined ? legacyDoc.aiAnalysis : undefined;
  const legacyPriceReview = legacyDoc && legacyDoc.priceReview ? legacyDoc.priceReview : undefined;
  const legacyFavorite = legacyDoc && legacyDoc.favorite;
  const legacyDeleted = legacyDoc && legacyDoc.deleted;
  const legacyStatus = legacyDoc && legacyDoc.status;
  const result: Lot = {
    id: overlay?.id ? overlay.id : doc.id || shared?.id || '',
    title,
    auctionTitle: payload.auctionTitle ? unescapeHtml(payload.auctionTitle) : undefined,
    source,
    auctionId,
    lotNumber: payload.lotNumber || undefined,
    itemNumber: payload.itemNumber || overlay?.itemNumber || undefined,
    currentBid: bidAmount,
    currentBidRaw: bidRaw,
    nextRequiredBid: nextBidAmount,
    nextRequiredBidRaw: nextBidRaw,
    lowSellValue: lowCurrency.amount,
    lowSellValueRaw: lowCurrency.raw,
    highSellValue: highCurrency.amount,
    highSellValueRaw: highCurrency.raw,
    lowListings,
    highListings,
    priceStatus: priceStatusValue,
    location: payload.location || undefined,
    shipping: payload.shipping || undefined,
    status: (overlay && overlay.status) || legacyStatus || 'received',
    closeAt,
    closeDateTime: closeRaw || undefined,
    liveFinish: payload.liveFinish || undefined,
    liveFinishDateRange: payload.liveFinishDateRange || undefined,
    auctioneer: payload.auctioneer || undefined,
    auctioneerLink: payload.auctioneerLink || undefined,
    auctioneerLogoUrl: payload.auctioneerLogoUrl || undefined,
    updatedAt: overlayUpdatedAt != null ? overlayUpdatedAt : sharedUpdatedAt,
    createdAt: overlayCreatedAt != null ? overlayCreatedAt : sharedCreatedAt,
    url: payload.url || undefined,
    imageUrl: payload.images && payload.images.length ? payload.images[0] : undefined,
    images: Array.isArray(payload.images) ? payload.images : [],
    tags: [],
    notes: payload.description ? unescapeHtml(payload.description) : undefined,
    remote: doc.remote === false ? false : true,
    favorite: overlay ? !!overlay.favorite : !!legacyFavorite,
    deleted: overlay ? !!overlay.deleted : !!legacyDeleted,
    bidding: overlay?.bidding ?? false,
    bidAmount: typeof overlay?.bidAmount === 'number' ? overlay.bidAmount : undefined,
    buyerPremiumPct:
      typeof payload.buyerPremiumPct === 'number' ? payload.buyerPremiumPct : undefined,
    buyerPremiumCap:
      typeof payload.buyerPremiumCap === 'number' ? payload.buyerPremiumCap : undefined,
    lifecycleStatus: (overlay?.lifecycleStatus ?? legacyDoc?.lifecycleStatus) || undefined,
    feeOverrides: cloneValue(overlay?.feeOverrides ?? legacyDoc?.feeOverrides) || undefined,
    targetMarginPct:
      typeof (overlay?.targetMarginPct ?? legacyDoc?.targetMarginPct) === 'number'
        ? (overlay?.targetMarginPct ?? legacyDoc?.targetMarginPct)
        : undefined,
    userMaxBid:
      typeof (overlay?.userMaxBid ?? legacyDoc?.userMaxBid) === 'number'
        ? (overlay?.userMaxBid ?? legacyDoc?.userMaxBid)
        : undefined,
    sellChannel: (overlay?.sellChannel ?? legacyDoc?.sellChannel) || undefined,
    outcome: cloneValue(overlay?.outcome ?? legacyDoc?.outcome) || undefined,
    marketplaceExports:
      cloneValue(overlay?.marketplaceExports ?? legacyDoc?.marketplaceExports) || undefined,
    aiStatus: typeof (shared && shared.aiStatus) === 'string' ? shared.aiStatus : legacyAiStatus,
    aiAttempts:
      typeof (shared && shared.aiAttempts) === 'number' ? shared.aiAttempts : legacyAiAttempts,
    aiRequestedAt:
      typeof (shared && shared.aiRequestedAt) === 'string'
        ? shared.aiRequestedAt
        : legacyAiRequestedAt,
    aiCompletedAt:
      typeof (shared && shared.aiCompletedAt) === 'string'
        ? shared.aiCompletedAt
        : legacyAiCompletedAt,
    aiError: typeof (shared && shared.aiError) === 'string' ? shared.aiError : legacyAiError,
    aiAnalysis:
      shared && shared.aiAnalysis !== undefined
        ? shared.aiAnalysis
        : legacyAiAnalysis !== undefined
          ? legacyAiAnalysis
          : null,
    priceReview: mapPriceReview(
      overlay && overlay.priceReview ? overlay.priceReview : legacyPriceReview
    ),
    sharedLot: cloneValue(shared),
    userOverlay: overlay ? cloneValue(overlay) : null,
  };
  return result;
}
